import { fitText, textWidth } from '../ComboChart/labels';

export interface FunnelStage {
  name: string;
  /** Null: the stage has no value; it draws no shape. */
  value: number | null;
  color: string;
  /** The value as text. */
  valueText: string;
  /** The rate from the stage before; null on the first stage. */
  stepText: string | null;
  /** The rate from the first stage; null on the first stage. */
  overallText: string | null;
}

export interface FunnelLayoutInput {
  width: number;
  stages: FunnelStage[];
  showValues: boolean;
  showRates: boolean;
  fontSize: number;
}

export interface FunnelRow {
  /**
   * The trapezoid's top and bottom widths, centered on the funnel's line:
   * the top is the stage's share of the largest, the bottom the next
   * stage's top. Both zero for no value.
   */
  topWidth: number;
  bottomWidth: number;
  /** The shape's top edge. */
  y: number;
  name: string;
  /** The value inside the shape when it fits, else past its right edge. */
  valueInside: boolean;
  /** Where a value beside the shape starts. */
  valueX: number;
  /** The value as drawn: cut to the room left before the rates. */
  value: string;
  step: string | null;
  overall: string | null;
}

export interface FunnelLayout {
  height: number;
  /** Each row's height; a shape fills it but for the gap below. */
  band: number;
  shapeHeight: number;
  /** Where the shapes' shared center line runs. */
  center: number;
  /** Where the names start: past the space left of a narrowed funnel. */
  nameX: number;
  /** The right column's left edge, where the rates read. */
  ratesX: number;
  rows: FunnelRow[];
}

export const kStepWord = '较上一步';
export const kOverallWord = '占首步';
/** The narrowest a stage with a value is drawn, so it stays visible. */
const kMinWidth = 4;
/** The gap between one stage and the next. */
const kGap = 2;
/**
 * How far the last stage narrows: with no next stage to meet, its bottom
 * is this share of its top.
 */
const kLastTaper = 0.6;
/**
 * The widest the funnel is drawn, as a multiple of its height: on a wide
 * card a full-width funnel reads as flat bars. The names, funnel and rates
 * then sit together in the middle of the card.
 */
const kMaxAspect = 2.6;

/**
 * A funnel of stacked trapezoids, each as wide at its top as its share of
 * the largest stage and narrowing to the next one's width: names to the
 * left, the rates of each stage to the right.
 */
export function funnelLayout(input: FunnelLayoutInput): FunnelLayout {
  const { width, stages, showValues, showRates, fontSize: f } = input;
  const band = Math.max(f * 4.4, 44);
  const shapeHeight = band - kGap;
  const nameWidth = Math.min(
    Math.max(...stages.map(stage => textWidth(stage.name, f)), 0) + 10,
    width * 0.28,
  );
  const rateTexts = stages.flatMap(stage => [
    stage.stepText ? `${kStepWord} ${stage.stepText}` : '',
    stage.overallText ? `${kOverallWord} ${stage.overallText}` : '',
  ]);
  const ratesWidth = showRates
    ? Math.min(
        Math.max(...rateTexts.map(text => textWidth(text, f)), 0) + 10,
        width * 0.32,
      )
    : 0;
  const room = Math.max(width - nameWidth - ratesWidth, 0);
  const plotWidth = Math.min(room, band * stages.length * kMaxAspect);
  const offset = (room - plotWidth) / 2;
  const plotLeft = offset + nameWidth;
  const center = plotLeft + plotWidth / 2;
  const peak = Math.max(
    ...stages.map(stage => (stage.value === null ? 0 : stage.value)),
    0,
  );
  const ratesX = plotLeft + plotWidth + 6;
  const tops = stages.map(stage =>
    stage.value === null
      ? 0
      : Math.max(
          plotWidth * (peak <= 0 ? 0 : Math.max(stage.value, 0) / peak),
          kMinWidth,
        ),
  );
  const rows = stages.map((stage, i): FunnelRow => {
    const topWidth = tops[i];
    const next = tops[i + 1];
    const bottomWidth = !topWidth
      ? 0
      : next
      ? next
      : Math.max(topWidth * kLastTaper, kMinWidth);
    // The value reads on the shape's middle line, where it is this wide.
    const middleWidth = (topWidth + bottomWidth) / 2;
    const inside = textWidth(stage.valueText, f) + 8 <= middleWidth;
    const valueX = center + middleWidth / 2 + 4;
    const valueRoom = ratesX - 6 - valueX;
    return {
      topWidth,
      bottomWidth,
      y: band * i,
      name: fitText(stage.name, nameWidth - 10, f),
      valueInside: showValues && inside,
      valueX,
      value: inside
        ? stage.valueText
        : fitText(stage.valueText, Math.max(valueRoom, f), f),
      step:
        showRates && stage.stepText
          ? fitText(`${kStepWord} ${stage.stepText}`, ratesWidth - 6, f)
          : null,
      overall:
        showRates && stage.overallText
          ? fitText(`${kOverallWord} ${stage.overallText}`, ratesWidth - 6, f)
          : null,
    };
  });
  return {
    height: band * stages.length,
    band,
    shapeHeight,
    center,
    nameX: offset,
    ratesX,
    rows,
  };
}
